import React, { useEffect, useState } from 'react';
import Button from 'antd/lib/button';
import Drawer from 'antd/lib/drawer';
import { CheckOutlined } from '@ant-design/icons';
import './styles.scss';
import { plural, t } from 'utils/i18n';
import {
    Candidate, Detection, displayName, previewUrl, resolveModel,
} from './reference-model';
import LibraryThumb from './library-thumb';

interface Props {
    detection: Detection;
    crop: string | null;
    backendAPI: string;
    busy: boolean;
    onUse: (candidate: Candidate) => void;
    onRemove: () => void;
    onSearchAgain?: () => void; // a drawing shape takes the photo picks, so it has no search
    onClose: () => void;
    subtitle?: string; // where the choices come from; a photo's are ranked by similarity
    extra?: React.ReactNode; // a drawing's "for all objects of this label"
    allowCurrent?: boolean; // the current model can be taken again, for the other objects
}

export default function ModelPicker(props: Props): JSX.Element {
    const {
        detection, crop, backendAPI, busy, onUse, onRemove, onSearchAgain, onClose, subtitle, extra, allowCurrent,
    } = props;
    const { candidates } = detection;
    const current = resolveModel(detection, backendAPI)?.candidate ?? null;
    const [selected, setSelected] = useState<Candidate | null>(null);

    // a different detection or a refreshed list must not keep a stale choice
    useEffect(() => setSelected(null), [detection.serverID, detection.clientID, candidates]);

    // a running search reloads the annotations afterwards, which would drop a change made now
    const canUseSelected = !busy && !!selected && (allowCurrent || selected !== current);
    const choose = (candidate: Candidate): void => { if (!busy) setSelected(candidate); };

    const footer = (
        <div className='cvat-references-picker-footer'>
            <div>
                {extra}
                {onSearchAgain && <Button type='link' loading={busy} onClick={onSearchAgain}>{t('Search again')}</Button>}
                <Button type='text' disabled={busy || !detection.value} onClick={onRemove}>{t('Remove model')}</Button>
            </div>
            <div>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <Button type='primary' disabled={!canUseSelected} onClick={() => selected && onUse(selected)}>
                    {t('Use selected')}
                </Button>
            </div>
        </div>
    );

    const title = (
        <div className='cvat-references-picker-title'>
            {crop ? <img className='cvat-references-crop' src={crop} alt='' /> :
                <div className='cvat-references-crop cvat-references-crop-placeholder' />}
            <div>
                <div className='cvat-references-picker-heading'>{t('Pick a model for: {label}', { label: detection.labelName })}</div>
                <div className='cvat-references-picker-subtitle'>
                    {subtitle ?? t('Best {count} · most similar first', { count: plural(candidates.length, 'match') })}
                </div>
            </div>
        </div>
    );

    return (
        <Drawer
            className='cvat-references-picker'
            visible
            forceRender
            placement='right'
            width={640}
            title={title}
            footer={footer}
            onClose={onClose}
            getContainer={() => document.querySelector('.cvat-workspace-content') ?? document.body}
            style={{ position: 'absolute' }}
        >
            {candidates.length === 0 ? (
                <div className='cvat-references-empty'>
                    {t('No matches stored yet. Use Search again to find models.')}
                </div>
            ) : (
                <div className='cvat-references-picker-grid'>
                    {candidates.map((candidate) => {
                        const url = previewUrl(candidate.image_path, backendAPI);
                        const isCurrent = candidate === current;
                        const classes = ['cvat-references-picker-card'];
                        if (isCurrent) classes.push('cvat-references-picker-card-current');
                        if (candidate === selected) classes.push('cvat-references-picker-card-selected');
                        return (
                            <div
                                key={candidate.path}
                                className={classes.join(' ')}
                                role='button'
                                tabIndex={0}
                                aria-pressed={candidate === selected}
                                aria-disabled={busy}
                                onClick={() => choose(candidate)}
                                onKeyDown={(event) => {
                                    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                                        event.preventDefault();
                                        choose(candidate);
                                    }
                                }}
                            >
                                {isCurrent && (
                                    <span className='cvat-references-picker-badge'>
                                        <CheckOutlined />
                                        {t('Current')}
                                    </span>
                                )}
                                <LibraryThumb
                                    className='cvat-references-picker-preview'
                                    src={url}
                                    fullSrc={previewUrl(candidate.image_path, backendAPI, true)}
                                />
                                <div
                                    className='cvat-references-picker-name'
                                    data-testid='model-card-name'
                                    title={displayName(candidate.name)}
                                >
                                    {displayName(candidate.name)}
                                </div>
                                <Button
                                    size='small'
                                    type='primary'
                                    className='cvat-references-picker-use'
                                    disabled={busy}
                                    onClick={(event) => { event.stopPropagation(); onUse(candidate); }}
                                >
                                    {t('Use this')}
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </Drawer>
    );
}
